import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { fetchWeightLogs } from '../../services/clientApi';

const CHART_ANIM = { duration: 2200, easing: 'ease-out', begin: 400 };

function WeightProgressComponent({ userCode, themeClasses, language, isDarkMode }) {
  const [weightLogs, setWeightLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('weight');
  const [timePeriod, setTimePeriod] = useState('all');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showAverage, setShowAverage] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showAddMeasurementModal, setShowAddMeasurementModal] = useState(false);
  const [measurementForm, setMeasurementForm] = useState({
    measurementType: 'weight',
    value: '',
    date: new Date().toISOString().split('T')[0],
    // Added for calculation support
    height: '',
    neck: '',
    gender: 'male'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadWeightLogs = async () => {
      if (!userCode) { setLoading(false); return; }
      setLoading(true);
      const { data, error } = await fetchWeightLogs(userCode);
      if (!error) setWeightLogs(data || []);
      setLoading(false);
    };
    loadWeightLogs();
  }, [userCode]);

  useEffect(() => {
    const checkOrientation = () => {
      const isMobileDevice = window.innerWidth < 768;
      setIsMobile(isMobileDevice);
      if (isMobileDevice) setIsLandscape(window.innerWidth > window.innerHeight);
      else setIsLandscape(false);
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  const handleAddMeasurement = async () => {
    if (!measurementForm.value || !measurementForm.date) return;
    setSaving(true);
    try {

      // Construct payload to match new server endpoint requirements
      const weightLogData = {
        user_code: userCode,
        measurement_date: measurementForm.date,
        height_cm: parseFloat(measurementForm.height),
        gender: measurementForm.gender,
        notes: measurementForm.notes
      };

      // Map specific measurements
      const val = parseFloat(measurementForm.value);
      if (measurementForm.measurementType === 'weight') weightLogData.weight_kg = val;
      if (measurementForm.measurementType === 'waist') weightLogData.waist_circumference_cm = val;
      if (measurementForm.measurementType === 'hip') weightLogData.hip_circumference_cm = val;
      if (measurementForm.measurementType === 'arm') weightLogData.arm_circumference_cm = val;
      if (measurementForm.measurementType === 'neck') weightLogData.neck_circumference_cm = val;

      const response = await fetch(`https://newdietitianweb-615263253386.europe-west3.run.app/api/weight-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(weightLogData),
      });

      if (response.ok) {
        const { data } = await fetchWeightLogs(userCode);
        setWeightLogs(data || []);
        setShowAddMeasurementModal(false);
        // Reset form
        setMeasurementForm({
          measurementType: 'weight', value: '', height: '', neck: '', gender: 'male',
          date: new Date().toISOString().split('T')[0]
        });
      } else {
        alert(language === 'hebrew' ? 'שגיאה בשמירת המדידה.' : 'Error saving measurement.');
      }
    } catch (error) {
      alert(language === 'hebrew' ? 'שגיאה בשמירת המדידה.' : 'Error saving measurement.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString(language === 'hebrew' ? 'he-IL' : 'en-US', { month: 'short', day: 'numeric' });
  const formatDateFull = (dateString) => new Date(dateString).toLocaleDateString(language === 'hebrew' ? 'he-IL' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const chartData = (() => {
    let filteredLogs = [...weightLogs];
    if (timePeriod !== 'all') {
      const cutoffDate = new Date();
      if (timePeriod === '1m') cutoffDate.setMonth(cutoffDate.getMonth() - 1);
      if (timePeriod === '3m') cutoffDate.setMonth(cutoffDate.getMonth() - 3);
      if (timePeriod === '6m') cutoffDate.setMonth(cutoffDate.getMonth() - 6);
      filteredLogs = weightLogs.filter(log => new Date(log.measurement_date) >= cutoffDate);
    }
    // Sort chronologically: oldest to newest
    filteredLogs.sort((a, b) => new Date(a.measurement_date) - new Date(b.measurement_date));
    return filteredLogs.map(log => ({
      date: formatDate(log.measurement_date), dateFull: log.measurement_date, weight: log.weight_kg,
      bodyFat: log.body_fat_percentage, waist: log.waist_circumference_cm, hip: log.hip_circumference_cm,
      arm: log.arm_circumference_cm, neck: log.neck_circumference_cm
    }));
  })();

  const getMetricValue = (d) => d[selectedMetric === 'body_fat' ? 'bodyFat' : selectedMetric] || d.weight;
  const currentValue = chartData.length > 0 ? getMetricValue(chartData[chartData.length - 1]) : null;

  const getMetricUnit = () => {
    if (selectedMetric === 'weight') return language === 'hebrew' ? 'ק"ג' : 'kg';
    if (selectedMetric === 'body_fat') return '%';
    return language === 'hebrew' ? 'ס"מ' : 'cm';
  };

  const averageValue = chartData.length > 0 ? chartData.map(getMetricValue).filter(v => v != null).reduce((a, b, _, arr) => a + b / arr.length, 0) : null;

  const metricDataKey = selectedMetric === 'body_fat' ? 'bodyFat' : selectedMetric;
  const metricLabel = {
    weight: language === 'hebrew' ? 'משקל' : 'Weight',
    body_fat: language === 'hebrew' ? 'אחוז שומן' : 'Body Fat',
    waist: language === 'hebrew' ? 'היקף מותניים' : 'Waist',
    hip: language === 'hebrew' ? 'היקף ירכיים' : 'Hip',
    arm: language === 'hebrew' ? 'היקף זרוע' : 'Arm',
    neck: language === 'hebrew' ? 'היקף צוואר' : 'Neck',
  }[selectedMetric];

  if (loading) return <div className={`${themeClasses.bgCard} rounded-lg p-6 mb-6 animate-pulse`}><div className="h-48 bg-moss rounded-lg"></div></div>;

  const hasData = chartData.length > 0;

  return (
    <div className={`${themeClasses.bgCard} rounded-lg p-4 sm:p-6 mb-6  border ${isDarkMode ? 'border-line' : 'border-line'}`}>

      {isMobile && (
        <div className={`${themeClasses.bgSecondary} rounded-lg p-3 mb-4 border border-forest/20`}>
          <div className="flex items-start gap-2">
            <span className="text-lg">💻</span>
            <p className={`${themeClasses.textSecondary} text-xs sm:text-sm flex-1`}>
              {language === 'hebrew'
                ? 'לצפייה טובה יותר, פתח את האתר במחשב'
                : 'For better viewing, open the website on a computer'}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-4 gap-3 overflow-hidden">
        <div className="flex-1 min-w-0">
          <h3 className={`${themeClasses.textPrimary} text-lg sm:text-xl font-bold`}>
            {language === 'hebrew' ? 'מעקב משקל והתקדמות' : 'Weight & Progress Tracking'}
          </h3>
          <div className="flex flex-wrap gap-2 sm:gap-4 mt-1">
            {currentValue != null && (
              <p className={`${themeClasses.textSecondary} text-xs sm:text-sm whitespace-nowrap`}>
                {language === 'hebrew' ? 'ערך נוכחי: ' : 'Current: '}
                <span className="font-semibold">
                  {currentValue.toFixed(selectedMetric === 'weight' || selectedMetric === 'body_fat' ? 1 : 0)} {getMetricUnit()}
                </span>
              </p>
            )}
            {averageValue != null && (
              <p className={`${themeClasses.textSecondary} text-xs sm:text-sm whitespace-nowrap`}>
                {language === 'hebrew' ? 'ממוצע: ' : 'Average: '}
                <span className="font-semibold">
                  {averageValue.toFixed(selectedMetric === 'weight' || selectedMetric === 'body_fat' ? 1 : 0)} {getMetricUnit()}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto sm:flex-shrink-0 min-w-0">
          <button
            onClick={() => setShowAddMeasurementModal(true)}
            className={`flex-1 sm:flex-none px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-forest-ondark hover:bg-forest-ondark/90 text-white dark:text-canvas-dark transition-colors text-xs sm:text-sm font-medium whitespace-nowrap min-w-0`}
          >
            {language === 'hebrew' ? 'הוסף מדידה' : 'Add Measurement'}
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className={`flex-1 sm:flex-none px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg ${themeClasses.bgSecondary} ${themeClasses.textPrimary} hover:${themeClasses.bgPrimary} transition-colors text-xs sm:text-sm font-medium whitespace-nowrap min-w-0`}
          >
            {expanded
              ? (language === 'hebrew' ? 'סגור' : 'Hide Options')
              : (language === 'hebrew' ? 'מידות נוספות' : 'More Measurements')
            }
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 items-center">
        {[
          { key: '1m', label: language === 'hebrew' ? 'חודש 1' : '1 Month' },
          { key: '3m', label: language === 'hebrew' ? '3 חודשים' : '3 Months' },
          { key: '6m', label: language === 'hebrew' ? '6 חודשים' : '6 Months' },
          { key: 'all', label: language === 'hebrew' ? 'כל הזמן' : 'All Time' }
        ].map(period => (
          <button
            key={period.key}
            onClick={() => { setIsTransitioning(true); setTimePeriod(period.key); setTimeout(() => setIsTransitioning(false), 300); }}
            className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${timePeriod === period.key
                ? 'bg-forest-ondark text-white dark:text-canvas-dark  '
                : `${themeClasses.bgSecondary} ${themeClasses.textSecondary} hover:${themeClasses.bgPrimary}`
              }`}
          >
            {period.label}
          </button>
        ))}
        <button
          onClick={() => setShowAverage(!showAverage)}
          className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${showAverage
              ? 'bg-forest-ondark text-white dark:text-canvas-dark  '
              : `${themeClasses.bgSecondary} ${themeClasses.textSecondary} hover:${themeClasses.bgPrimary}`
            }`}
        >
          {language === 'hebrew' ? 'ממוצע' : 'Average'}
        </button>
      </div>

      {expanded && (
        <div className="mb-4 flex flex-wrap gap-2">
          {[
            { key: 'weight', label: language === 'hebrew' ? 'משקל' : 'Weight', unit: 'kg' },
            { key: 'body_fat', label: language === 'hebrew' ? 'אחוז שומן' : 'Body Fat', unit: '%' },
            { key: 'waist', label: language === 'hebrew' ? 'היקף מותניים' : 'Waist', unit: 'cm' },
            { key: 'hip', label: language === 'hebrew' ? 'היקף ירכיים' : 'Hip', unit: 'cm' },
            { key: 'arm', label: language === 'hebrew' ? 'היקף זרוע' : 'Arm', unit: 'cm' },
            { key: 'neck', label: language === 'hebrew' ? 'היקף צוואר' : 'Neck', unit: 'cm' }
          ].map(metric => (
            <button
              key={metric.key}
              onClick={() => { setIsTransitioning(true); setSelectedMetric(metric.key); setTimeout(() => setIsTransitioning(false), 300); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedMetric === metric.key
                  ? 'text-white  '
                  : `${themeClasses.bgSecondary} ${themeClasses.textSecondary} hover:${themeClasses.bgPrimary}`
                }`}
              style={selectedMetric === metric.key ? { backgroundColor: 'var(--forest-ondark)' } : {}}
            >
              {metric.label}
            </button>
          ))}
        </div>
      )}

      {isLandscape && (
        <div className={`${themeClasses.bgCard} rounded-lg p-8 mb-6 border-2 border-forest/50 `} style={{ minHeight: '200px' }}>
          <div className="flex flex-col items-center justify-center text-center h-full">
            <div className="text-6xl mb-4 animate-bounce">📱</div>
            <h3 className={`${themeClasses.textPrimary} text-xl sm:text-2xl font-bold mb-3`}>
              {language === 'hebrew' ? 'סובב את הטלפון' : 'Rotate Your Phone'}
            </h3>
            <p className={`${themeClasses.textSecondary} text-base sm:text-lg max-w-md`}>
              {language === 'hebrew'
                ? 'אנא סובב את הטלפון שלך למצב אופקי (Landscape) כדי לראות את הגרף'
                : 'Please rotate your phone to landscape mode (horizontal) to view the chart'}
            </p>
          </div>
        </div>
      )}

      {!isLandscape && (
        <div className={`rounded-lg border p-4  ${isDarkMode ? 'border-line bg-surface/80' : 'border-line bg-surface'}`}>
          {!hasData ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-4xl mb-3">📊</div>
              <p className={`${themeClasses.textSecondary} text-sm sm:text-base mb-4 text-center px-4`}>
                {language === 'hebrew' ? 'אין לך רשומות מדידות גוף' : "You don't have any Body Measurements Log"}
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 12, bottom: 24 }}>
                <defs>
                  <linearGradient id="gradWeight" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--forest-ondark)" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="var(--forest-ondark)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="date" reversed={language === 'hebrew'} tick={{ fontSize: 11, fill: 'var(--ink-muted)' }} />
                <YAxis orientation={language === 'hebrew' ? 'right' : 'left'} tick={{ fontSize: 11, fill: 'var(--ink-muted)' }} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--surface)',
                    border: '1px solid var(--line)',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: 'var(--ink)' }}
                  formatter={(value) => [value != null ? value.toFixed(selectedMetric === 'weight' || selectedMetric === 'body_fat' ? 1 : 0) + ' ' + getMetricUnit() : '', metricLabel]}
                  labelFormatter={(label) => formatDateFull(chartData.find(d => d.date === label)?.dateFull || label)}
                />
                <Legend wrapperStyle={{ color: 'var(--ink-muted)' }} />
                {showAverage && averageValue != null && (
                  <ReferenceLine
                    y={averageValue}
                    stroke="var(--forest-ondark)"
                    strokeDasharray="6 4"
                    strokeOpacity={0.7}
                    label={{ value: language === 'hebrew' ? 'ממוצע' : 'Avg', position: language === 'hebrew' ? 'left' : 'right', fill: 'var(--forest-ondark)' }}
                  />
                )}
                <Area
                  type="monotone"
                  dataKey={metricDataKey}
                  name={metricLabel}
                  stroke="var(--forest-ondark)"
                  fill="url(#gradWeight)"
                  strokeWidth={2}
                  dot={{ fill: 'var(--forest-ondark)', strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5, strokeWidth: 2, fill: 'var(--forest-ondark)' }}
                  isAnimationActive={true}
                  animationDuration={CHART_ANIM.duration}
                  animationEasing={CHART_ANIM.easing}
                  animationBegin={CHART_ANIM.begin}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Mobile-Friendly Modal */}
      {showAddMeasurementModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80  p-4" onClick={() => !saving && setShowAddMeasurementModal(false)}>
          <div className={`${themeClasses.bgCard} rounded-lg p-5 sm:p-6 max-w-md w-full  overflow-y-auto max-h-[90vh]`} onClick={e => e.stopPropagation()}>
            <h3 className={`${themeClasses.textPrimary} text-lg sm:text-xl font-bold mb-4`}>
              {language === 'hebrew' ? 'הוסף מדידה' : 'Add Measurement'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className={`block ${themeClasses.textPrimary} text-sm font-medium mb-2`}>
                  {language === 'hebrew' ? 'סוג מדידה' : 'Measurement Type'}
                </label>
                <select
                  value={measurementForm.measurementType}
                  onChange={(e) => setMeasurementForm(prev => ({ ...prev, measurementType: e.target.value, value: '' }))}
                  className={`w-full px-3 py-2 rounded-lg ${themeClasses.bgSecondary} ${themeClasses.textPrimary} border ${isDarkMode ? 'border-line' : 'border-line'} focus:outline-none focus:ring-2 focus:ring-forest`}
                  disabled={saving}
                >
                  <option value="weight">{language === 'hebrew' ? 'משקל (ק"ג)' : 'Weight (kg)'}</option>
                  <option value="body_fat">{language === 'hebrew' ? 'אחוז שומן (%)' : 'Body Fat (%)'}</option>
                  <option value="waist">{language === 'hebrew' ? 'היקף מותניים (ס"מ)' : 'Waist (cm)'}</option>
                  <option value="hip">{language === 'hebrew' ? 'היקף ירכיים (ס"מ)' : 'Hip (cm)'}</option>
                  <option value="arm">{language === 'hebrew' ? 'היקף זרוע (ס"מ)' : 'Arm (cm)'}</option>
                  <option value="neck">{language === 'hebrew' ? 'היקף צוואר (ס"מ)' : 'Neck (cm)'}</option>
                </select>
              </div>

              <div>
                <label className={`block ${themeClasses.textPrimary} text-sm font-medium mb-2`}>
                  {language === 'hebrew' ? 'ערך' : 'Value'}
                </label>
                <input
                  type="number"
                  step={measurementForm.measurementType === 'weight' || measurementForm.measurementType === 'body_fat' ? '0.1' : '0.1'}
                  value={measurementForm.value}
                  onChange={(e) => setMeasurementForm(prev => ({ ...prev, value: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg ${themeClasses.bgSecondary} ${themeClasses.textPrimary} border ${isDarkMode ? 'border-line' : 'border-line'} focus:outline-none focus:ring-2 focus:ring-forest`}
                  placeholder={language === 'hebrew' ? 'הכנס ערך' : 'Enter value'}
                  disabled={saving}
                />
              </div>
              {/* Add this inside your Modal's space-y-4 div */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block ${themeClasses.textPrimary} text-xs font-medium mb-1`}>
                    {language === 'hebrew' ? 'גובה (ס"מ)' : 'Height (cm)'}
                  </label>
                  <input
                    type="number"
                    value={measurementForm.height}
                    onChange={(e) => setMeasurementForm(prev => ({ ...prev, height: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg ${themeClasses.bgSecondary} border ${isDarkMode ? 'border-line' : 'border-line'}`}
                    placeholder="175"
                  />
                </div>
                <div>
                  <label className={`block ${themeClasses.textPrimary} text-xs font-medium mb-1`}>
                    {language === 'hebrew' ? 'מין' : 'Gender'}
                  </label>
                  <select
                    value={measurementForm.gender}
                    onChange={(e) => setMeasurementForm(prev => ({ ...prev, gender: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg ${themeClasses.bgSecondary} border ${isDarkMode ? 'border-line' : 'border-line'}`}
                  >
                    <option value="male">{language === 'hebrew' ? 'גבר' : 'Male'}</option>
                    <option value="female">{language === 'hebrew' ? 'אישה' : 'Female'}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={`block ${themeClasses.textPrimary} text-sm font-medium mb-2`}>
                  {language === 'hebrew' ? 'תאריך' : 'Date'}
                </label>
                <input
                  type="date"
                  value={measurementForm.date}
                  onChange={(e) => setMeasurementForm(prev => ({ ...prev, date: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg ${themeClasses.bgSecondary} ${themeClasses.textPrimary} border ${isDarkMode ? 'border-line' : 'border-line'} focus:outline-none focus:ring-2 focus:ring-forest`}
                  disabled={saving}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => !saving && setShowAddMeasurementModal(false)}
                  disabled={saving}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${themeClasses.bgSecondary} ${themeClasses.textPrimary} hover:${themeClasses.bgPrimary} ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {language === 'hebrew' ? 'ביטול' : 'Cancel'}
                </button>
                <button
                  onClick={handleAddMeasurement}
                  disabled={saving || !measurementForm.value || !measurementForm.date}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors bg-forest-ondark hover:bg-forest-ondark/90 text-white dark:text-canvas-dark ${saving || !measurementForm.value || !measurementForm.date ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {saving ? (language === 'hebrew' ? 'שומר...' : 'Saving...') : (language === 'hebrew' ? 'שמור' : 'Save')}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WeightProgressComponent;